import React, { useMemo, useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Library from '../components/Library';
import InboxList from '../components/InboxList';
import ThoughtDetail from '../components/ThoughtDetail';
import ComposePage from '../components/ComposePage';
import FieldEditPage from '../components/FieldEditPage';
import FloatingActionButton from '../components/FloatingActionButton';
import ThoughtsList from '../components/ThoughtsList';
import AddToCollectionPage from './AddToCollectionPage';
import AddToCollectionsPage from './AddToCollectionsPage';
import useCollectionThoughts from '../hooks/useCollectionThoughts';
import Tabs from '../components/Tabs';
import DraftsReview from '../components/DraftsReview';
import { formatTagName } from '../utils/formatters';
import useWindowResize from '../hooks/useWindowResize';
import useIntentionThoughts from '../hooks/useIntentionThoughts';
import { isDraftThought } from '../utils/draftPredicate';
import { useReviewMode } from '../contexts/ReviewModeContext';

const AppContent = ({ 
  activeTab, 
  setActiveTab, 
  thoughts, 
  saveThought, 
  deleteThought, 
  updateThoughtLocally, 
  themes, 
  motifs, 
  tags, 
  isDataLoading
}) => {
  const isMobile = useWindowResize();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const { reviewing, setReviewing } = useReviewMode();
  
  // Check if on detail page (to remove padding when header is hidden)
  const isDetailPage = location.pathname.includes('/thought/') || 
                      location.pathname.includes('/edit/') ||
                      location.pathname.includes('/edit-field/') ||
                      location.pathname.includes('/compose') ||
                      location.pathname.includes('/collection/') ||
                      location.pathname.includes('/timeline');
  
  // Filter thoughts for the inbox view — single source of truth lives in
  // utils/draftPredicate so the predicate stays in lockstep with the
  // queue hook and the AI prefetch guard.
  const inboxThoughts = useMemo(
    () => thoughts.filter(isDraftThought),
    [thoughts]
  );

  // The tag vocabulary used to FILTER AI suggestions. This must be the user's
  // FULL tag vocabulary (`tags`, fetched by getThoughtVocabulary across ALL
  // rows), NOT getAllUniqueTags(thoughts) — `thoughts` is the drafts-only home
  // array, and the KNN suggester votes tags from the user's entire tagged
  // history (most of which live on triaged thoughts). Filtering suggestions
  // against the drafts-only vocab silently dropped most valid suggestions.
  const allTagsMemo = tags;

  // The home page only loads draft thoughts, so the ?intention= view can no
  // longer filter the global array (intention-tagged thoughts are triaged and
  // absent from it). Query them directly instead. Hook is called
  // unconditionally (rules of hooks) and no-ops when no intention is active.
  const intentionFilter = new URLSearchParams(location.search).get('intention');
  const { intentionThoughts } = useIntentionThoughts(intentionFilter);

  // Handle deleting a thought
  const handleDeleteThought = (thoughtId) => {
    // Use the deleteThought function passed from App.js
    if (deleteThought && thoughtId) {
      deleteThought(thoughtId);
    }
  };

  // Handle navigation with fade effect
  const handleNavigateWithFade = (to, state = {}) => {
    if (isAnimating) return; // Prevent multiple animations
    setIsAnimating(true);
    
    // Fix for thought IDs - if the path is just an ID (no slashes), add the /thought/ prefix
    if (to && !to.includes('/')) {
      to = `/thought/${to}`;
    }
    
    // Apply fade out animation
    document.body.classList.add('fade-out');
    
    // Navigate after animation completes
    setTimeout(() => {
      navigate(to, { state });
      document.body.classList.remove('fade-out');
      setIsAnimating(false);
    }, 180); // Match the animation duration
  };

  // Handle back to library with fade
  const handleBackToLibrary = () => {
    handleNavigateWithFade('/');
    setActiveTab("library");
  };

  return (
    <>
      <style>
        {`
          .fade-out {
            animation: fadeOut 0.18s cubic-bezier(0.2, 0, 0, 1) forwards;
          }
          
          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
        `}
      </style>
      
      <div
        className="main-content-container"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          // Drafts review hides the top header, so reclaim the 60px it would have left.
          paddingTop: (isDetailPage || reviewing) ? '0' : '60px',
          marginTop: 0,
        }}
      >

        {/* Main content area - dynamically changes based on active tab */}
        <div style={{
          flex: 1,
          overflow: reviewing ? 'hidden' : 'auto',
          padding: reviewing ? 0 : (isMobile ? '8px' : '16px 24px'),
          paddingBottom: reviewing ? 0 : '80px', // Extra padding for FAB
        }}>
          <Routes>
            <Route path="/" element={
              <>
                {/* Check for intention parameter */}
                {(() => {
                  if (intentionFilter) {
                    // intentionThoughts is fetched directly by useIntentionThoughts
                    // (above) — the global array is drafts-only and no longer
                    // contains triaged, intention-tagged thoughts.
                    return (
                      <ThoughtsList
                        thoughts={intentionThoughts}
                        title='Intentions'
                        onDeleteThought={handleDeleteThought}
                        onBackClick={() => navigate('/intentions')}
                        showBackButton={true}
                      />
                    );
                  }
                  
                  // Drafts review takes over the surface — peer to InboxList,
                  // not nested inside it, so the tabs/FAB/header above can hide.
                  if (reviewing) {
                    return (
                      <DraftsReview
                        thoughts={thoughts}
                        allTags={allTagsMemo}
                        onSave={saveThought}
                        updateThoughtLocally={updateThoughtLocally}
                        onExit={() => setReviewing(false)}
                        deleteThought={handleDeleteThought}
                      />
                    );
                  }

                  // Default behavior - show tabs
                  return (
                    <>
                      {/* Tabs for all devices */}
                      <Tabs
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                      />
                      {/* Content based on active tab */}
                      {activeTab === "inbox" ? (
                        <InboxList
                          thoughts={inboxThoughts}
                          emptyMessage="Enjoy the mental pause"
                          onDeleteThought={handleDeleteThought}
                          onNavigate={handleNavigateWithFade}
                          isLoading={isDataLoading}
                        />
                      ) : (
                        <Library
                          navigateToCollection={(collectionId, collectionName, isDefault, isTagCollection) =>
                            handleNavigateWithFade(`/collection/${collectionId}`, {
                              collectionName,
                              isDefault,
                              isTagCollection
                            })
                          }
                          tags={tags}
                        />
                      )}
                    </>
                  );
                })()}
              </>
            } />
            <Route path="/thought/:id" element={
              <ThoughtDetail
                thoughts={thoughts}
                allTags={allTagsMemo}
                onSave={saveThought}
                updateThoughtLocally={updateThoughtLocally}
                isDataLoading={isDataLoading}
                deleteThought={handleDeleteThought}
              />
            } />
            <Route path="/edit/:id" element={
              <ComposePage 
                thoughts={thoughts}
                onSave={saveThought}
                updateThoughtLocally={updateThoughtLocally}
                isDataLoading={isDataLoading}
                isEditing={true}
              />
            } />
            <Route path="/edit-field/:id/:fieldName" element={
              <FieldEditPage 
                thoughts={thoughts}
                onSave={saveThought}
                themes={themes}
                motifs={motifs}
                tags={tags}
                isDataLoading={isDataLoading}
                updateThoughtLocally={updateThoughtLocally}
              />
            } />
            <Route path="/compose" element={
              <ComposePage 
                thoughts={thoughts}
                onSave={saveThought}
                updateThoughtLocally={updateThoughtLocally}
                isDataLoading={isDataLoading}
              />
            } />
            <Route path="/thought/:thoughtId/collections" element={
              <AddToCollectionsPage 
                navigateWithFade={handleNavigateWithFade}
              />
            } />
            <Route path="/collection/:collectionId" element={
              <CollectionView
                onDeleteThought={handleDeleteThought}
                onBackClick={handleBackToLibrary}
                navigateWithFade={handleNavigateWithFade}
              />
            } />
            <Route path="/collection/:collectionId/add" element={
              <AddToCollectionPage 
                thoughts={thoughts}
                navigateWithFade={handleNavigateWithFade}
              />
            } />
          </Routes>
        </div>
        
        {/* Floating Action Button (hidden on detail pages and during drafts review) */}
        {!isDetailPage && !reviewing && (
          <FloatingActionButton />
        )}
      </div>
    </>
  );
};

// Collection View Component - Renders a collection without tabs
const CollectionView = ({ onDeleteThought, onBackClick, navigateWithFade }) => {
  const location = useLocation();
  const { state } = location;
  
  // Extract collection ID from the URL
  const collectionId = location.pathname.split('/collection/')[1];
  const collectionName = state?.collectionName || "Collection";
  const isDefault = state?.isDefault || false;
  const isTagCollection = state?.isTagCollection || false;
  
  // Format collection name if it's a tag collection
  const displayName = isTagCollection ? formatTagName(collectionName) : collectionName;
  
  const { collectionThoughts, loading: isLoading, setCollectionThoughts } = useCollectionThoughts(
    collectionId,
    isDefault
  );
  
  // Handle back with animation
  const handleBack = () => {
    onBackClick();
  };
  
  // Handle adding thoughts to collection
  const handleAddToCollection = () => {
    navigateWithFade(`/collection/${collectionId}/add`, {
      collectionName,
      isDefault,
      isTagCollection
    });
  };
  
  // Handle thought deletion - differently for default vs. custom collections
  const handleDeleteThought = async (thoughtId) => {
    if (isDefault) {
      // For the default collection, actually delete the thought from database.
      onDeleteThought(thoughtId);
      // Prune the displayed list too: collectionThoughts is now the hook's own
      // getThoughts fetch (decoupled from the parent `thoughts` array), so it no
      // longer updates on its own when the parent deletes — mirror the
      // non-default branch's optimistic removal.
      setCollectionThoughts(prevThoughts =>
        prevThoughts.filter(thought => thought.id !== thoughtId)
      );
    } else {
      try {
        // For non-default collections, just remove it from the collection
        const { removeThoughtFromCollection } = await import('../services/collectionService');
        await removeThoughtFromCollection(collectionId, thoughtId);
        
        // Update local state to remove the thought from the displayed list
        setCollectionThoughts(prevThoughts => 
          prevThoughts.filter(thought => thought.id !== thoughtId)
        );
      } catch (error) {
        console.error('Error removing thought from collection:', error);
      }
    }
  };
  
  // Show loading state while fetching collection thoughts
  if (isLoading) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        color: 'rgba(0,0,0,0.6)' 
      }}>
        Loading collection...
      </div>
    );
  }
  
  return (
    <div className="collection-view-container" style={{ width: '100%' }}>
      <ThoughtsList
        thoughts={collectionThoughts}
        title={displayName}
        onDeleteThought={handleDeleteThought}
        onBackClick={handleBack}
        showBackButton={true}
        onAddClick={!isDefault ? handleAddToCollection : undefined}
      />
    </div>
  );
};

export default AppContent; 